import {
  JSONPatchDocument,
  PagedResponse,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { FoiItem } from "../domain/FoiItem";
import { MODULE_NAME } from "../moduleName";
import { foiService, shareService } from "../services";
import {
  ActionPayload,
  addCaseNoteError,
  addFoiEventError,
  ADD_CASE_NOTE,
  ADD_INTERNAL_MESSAGE,
  ASSIGN_FOI,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedItem,
  loadedItems,
  LOADING_ITEM,
  LOADING_ITEMS,
  UNASSIGN_FOI,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  CANCEL_FOI,
  CLOSE_FOI,
  REOPEN_FOI,
  loadingItem,
  ADD_FOI_EVENT,
  COMPLETE_FOI_EVENT,
  UPDATING_SHARE,
  loadedShare,
  UPDATE_STAGE,
  ADD_PUBLIC_MESSAGE,
} from "./actions";
import { authSelector, feedSelector } from "./selectors";
import { History } from "history";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { Attachment } from "../domain/Attachment";
import { ShareRecord } from "../domain/ShareRecord";

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchAddCaseNoteSaga() {
  yield takeLatest(ADD_CASE_NOTE, addCaseNoteSaga);
}

function* watchAddInternalMessage() {
  yield takeLatest(ADD_INTERNAL_MESSAGE, addInternalMessage);
}

function* watchAddPublicMessage() {
  yield takeLatest(ADD_PUBLIC_MESSAGE, addPublicMessage);
}

function* watchAssignFoiSaga() {
  yield takeLatest(ASSIGN_FOI, assignFoiSaga);
}

function* watchUnassignFoiSaga() {
  yield takeLatest(UNASSIGN_FOI, unassignFoiSaga);
}

function* watchCloseFoiSaga() {
  yield takeLatest(CLOSE_FOI, closeFoiSaga);
}

function* watchCancelFoiSaga() {
  yield takeLatest(CANCEL_FOI, cancelFoiSaga);
}

function* watchReOpenFoiSaga() {
  yield takeLatest(REOPEN_FOI, reOpenFoiSaga);
}

function* watchPutFoiEventSaga() {
  yield takeLatest(ADD_FOI_EVENT, putFoiEventByItemIdSaga);
}

function* watchCompleteFoiEventSaga() {
  yield takeLatest(COMPLETE_FOI_EVENT, completeFoiEventByItemIdSaga);
}

function* watchUpdateShareSaga() {
  yield takeLatest(UPDATING_SHARE, updateShareSaga);
}

function* watchUpdateStage() {
  yield takeLatest(UPDATE_STAGE, updateStageSaga);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<FoiItem> = yield call(
    [foiService, foiService.search],
    { ...payload },
    { token: auth.token }
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload, action }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const opts = { token: auth.token };

  const { item, share } = yield all({
    item: call([foiService, foiService.get], payload, opts),
    share: call([shareService, shareService.get], payload, opts),
  });

  yield put(loadedItem(item, share));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);

  const { id, patchDocument, documentsToDelete, newFiles } = payload as {
    id: string;
    patchDocument: JSONPatchDocument;
    documentsToDelete: Attachment[];
    newFiles: File[];
  };

  const deleteAttachmentsCalls = () =>
    Promise.all(
      documentsToDelete.map((doc) =>
        foiService.deleteAttachment(id, doc.blobId, {
          token: token,
        })
      )
    );

  const attachmentsCall = () => {
    newFiles && newFiles.length
      ? foiService.postAttachments(id, newFiles, {
          token: token,
        })
      : Promise.resolve();
  };

  const { validatedResponse } = yield all({
    validatedResponse: call(
      [foiService, foiService.update],
      id,
      patchDocument,
      {
        token,
      }
    ),
    deletedDocs: call(deleteAttachmentsCalls),
    newDocs: call(attachmentsCall),
  });

  if (validatedResponse.isValid) {
    yield put(loadingItem(id));
    yield put(refreshActivityFeed(id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createItemSaga({ payload }: ActionPayload<FoiItem>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const validatedResponse: ValidatedResponse<FoiItem> = yield call(
    [foiService, foiService.create],
    payload,
    {
      token: auth.token,
    }
  );
  if (validatedResponse.isValid) {
    const id = validatedResponse.data.id;
    yield call([foiService, foiService.postAttachments], id, payload.foiFiles, {
      token: auth.token,
    });

    yield put({ type: CREATED_ITEM });
    yield call(() => navigate(`/${MODULE_NAME}/${validatedResponse.data.id}`));
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* addCaseNoteSaga({
  payload,
}: ActionPayload<{
  id: string;
  caseFiles: File[];
  caseNote: string;
  subject: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, caseFiles, caseNote, subject } = payload;
  try {
    yield call(
      [foiService, foiService.addCaseNote],
      id,
      caseFiles,
      caseNote,
      subject,
      { token }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadingItem(id));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This enquiry has been closed"));
    } else {
      throw err;
    }
  }
}

function* addInternalMessage({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  subject: string;
  message: string;
  recipients: string[];
}>) {
  const { token } = yield select(authSelector);
  const { id, files, subject, message, recipients } = payload;
  try {
    yield call(
      [foiService, foiService.addInternalMessage],
      id,
      files,
      subject,
      message,
      recipients,
      { token }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadingItem(id));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This FOI has been closed"));
    } else {
      throw err;
    }
  }
}

function* addPublicMessage({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  subject: string;
  message: string;
  responseTypeId: number;
  formalResponseTypeId?: number;
}>) {
  const { token } = yield select(authSelector);
  const { id, files, subject, message, responseTypeId, formalResponseTypeId } = payload;
  try {
    yield call(
      [foiService, foiService.addAddPublicMessage],
      id,
      files,
      subject,
      message,
      responseTypeId,
      formalResponseTypeId ?? null,
      { token }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadingItem(id));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This FOI has been closed"));
    } else {
      throw err;
    }
  }
}

function* assignFoiSaga({
  payload,
}: ActionPayload<{
  id: string;
  upn: string;
  displayName: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, upn, displayName } = payload;

  try {
    const foi: FoiItem = yield call(
      [foiService, foiService.assignFoi],
      id,
      upn,
      displayName,
      { token }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(foi));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This FOI has been closed"));
    } else {
      throw err;
    }
  }
}

function* unassignFoiSaga({
  payload,
}: ActionPayload<{
  id: string;
}>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  try {
    const foi: FoiItem = yield call([foiService, foiService.unassignFoi], id, {
      token,
    });

    yield put(refreshActivityFeed(id));
    yield put(loadedItem(foi));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This FOI has been closed"));
    } else {
      throw err;
    }
  }
}

function* closeFoiSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  try {
    const foi: FoiItem = yield call([foiService, foiService.closeFoi], id, {
      token,
    });
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(foi));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This FOI is already closed"));
    } else {
      throw err;
    }
  }
}

function* cancelFoiSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  try {
    const foi: FoiItem = yield call([foiService, foiService.cancelFoi], id, {
      token,
    });
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(foi));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addCaseNoteError("This FOI is already closed"));
    } else {
      throw err;
    }
  }
}

function* reOpenFoiSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const foi: FoiItem = yield call([foiService, foiService.reOpenFoi], id, {
    token,
  });
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(foi));
}

function* updateStageSaga({
  payload,
}: ActionPayload<{ id: string; foiStage: string }>) {
  const { token } = yield select(authSelector);
  const { id, foiStage } = payload;

  const foi: FoiItem = yield call(
    [foiService, foiService.updateStage],
    id,
    foiStage,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(foi));
}

function* putFoiEventByItemIdSaga({ payload }: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  try {
    yield call(
      [foiService, foiService.putFoiEventByItemId],
      payload.foiId,
      payload.newEvent,
      {
        token,
      }
    );

    yield put(refreshActivityFeed(payload.foiId));
    yield put(loadingItem(payload.foiId));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(addFoiEventError("This FOI has been closed"));
    } else {
      throw err;
    }
  }
}

function* completeFoiEventByItemIdSaga({ payload }: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  yield call(
    [foiService, foiService.completeFoiEventByItemId],
    payload.foiId,
    payload.foiEventId,
    payload.completionEvent,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(payload.foiId));
}

function* updateShareSaga({
  payload,
}: ActionPayload<{ id: string; share: ShareRecord }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, share } = payload;

  const response: ShareRecord = yield call(
    [shareService, shareService.update],
    id,
    share,
    { token }
  );

  const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
  const params = feedState?.[id]?.params;
  yield put(refreshActivityFeed(id, params));
  yield put(loadedShare(response));
}

export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchAddCaseNoteSaga(),
    watchAddInternalMessage(),
    watchAssignFoiSaga(),
    watchUnassignFoiSaga(),
    watchCloseFoiSaga(),
    watchCancelFoiSaga(),
    watchReOpenFoiSaga(),
    watchPutFoiEventSaga(),
    watchCompleteFoiEventSaga(),
    watchUpdateShareSaga(),
    watchUpdateStage(),
    watchAddPublicMessage(),
    // further sagas...
  ]);
}
